import { useEffect, useState } from 'react';
import styled from 'styled-components';

import MovieList from 'components/list/MovieList';
import NoSearchData from './NoSearchData';
import DefaultButton from 'components/button/DefaultButton';
import Paginav from 'components/paginav/Pagination';
import DefaultInput from 'components/input/DefaultInput';
import DefaultSelectMenu from 'components/selectMenu/DefaultSelectMenu';
import { useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import companyInforAtom from 'atom/companyInfoAtom';
import { useAtomValue } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataWrapper } from 'components/skeleton/ReferenceSkeleton';

interface ReferenceProps {
  boardData: BoardListData[];
}

interface MenuData {
  key: number;
  label: string;
  value: number;
}

const Reference = ({ boardData }: ReferenceProps) => {
  const companyInfor = useAtomValue(companyInforAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);

  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
  const [searchInputValue, setSearchInputValue] = useState('');

  const [pageNumber, setPageNumber] = useState(0);

  const [selectedMenuIdx, setSelectedMenuIdx] = useState<number>(
    Number(urlSearchParams.get('menu'))
  );

  const [selectedValue, setSelectedValue] = useState<number>(
    Number(urlSearchParams.get('submenu'))
  );

  const [centerData, setCenterData] = useState<MenuData[]>([]);

  const handleButtonClick = (index: number) => {
    setSelectedButtonIndex(index);
    setSearchInputValue('');
    setPageNumber(0);
  };

  const handleChoiceSelectBox = (selectedIndex: string | number) => {
    const index: number =
      typeof selectedIndex === 'string' ? parseInt(selectedIndex) : selectedIndex;
    const selectedKey: number = centerData[index]?.key;

    setSelectedValue(selectedKey);
  };

  const findLabelBySelectedValue = (selectedValue: number) => {
    const foundItem = centerData?.find((item) => item.key === selectedValue);
    return foundItem ? foundItem.label : 'Label not found';
  };

  const selectedLabel = findLabelBySelectedValue(selectedValue);

  const handleSearch = (
    searchValue: string,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLInputElement>,
    info?: { source?: 'clear' | 'input' }
  ) => {
    if (info?.source === 'input') {
      boardSearchListApi.refetch();
    }
  };

  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  const handleChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInputValue(value);
  };

  const params = {
    categoryidx: companyInfor.categoryidx,
    countrycode: 'KR',
    menuidx: selectedMenuIdx,
    submenuidx: selectedValue,
    keyword: searchInputValue,
    page: pageNumber
  };

  const boardSearchListApi = useQuery({
    queryKey: [QueryKeys.BOARD_SEARCH, params],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/board/search',
        params: params
      })
  });

  const { data: boardList } = boardSearchListApi;

  const boardSearchList: BoardSearchList[] = boardList && boardList.data?.list;

  useEffect(() => {
    if (companyInfor.categoryidx !== 0 && selectedValue !== 0) {
      boardSearchListApi.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfor.categoryidx, selectedMenuIdx, selectedValue, pageNumber]);

  useEffect(() => {
    const SelctedBoxData: BoardChildListData[] = boardData[selectedButtonIndex]?.childMenuList;

    const data = SelctedBoxData?.map((menu, index) => ({
      key: menu?.menuidx,
      label: menu?.menuname,
      value: index
    }));

    setCenterData(data);
  }, [boardData, selectedButtonIndex]);

  useEffect(() => {
    if (selectedValue !== 0 && selectedMenuIdx !== 0) {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set('menu', selectedMenuIdx.toString());
      urlSearchParams.set('submenu', selectedValue.toString());

      navigate(`${window.location.pathname}?${urlSearchParams.toString()}`, { replace: true });
    }
  }, [selectedMenuIdx, selectedValue, pageNumber, navigate]);

  useEffect(() => {
    setSelectedMenuIdx(boardData[selectedButtonIndex]?.menuidx || 0);
    setSelectedValue(boardData[selectedButtonIndex]?.childMenuList[0]?.menuidx || 0);
  }, [boardData, selectedButtonIndex]);

  return (
    <>
      <ButtonWrapper>
        {boardData?.map((item, index) => (
          <DefaultButton
            key={index}
            text={item.menuname}
            type={selectedButtonIndex === index ? 'primary' : 'default'}
            onClick={() => handleButtonClick(index)}
          />
        ))}
      </ButtonWrapper>
      <InputWrapper>
        <DefaultInput
          name="search"
          search
          value={searchInputValue}
          onChange={handleChangeSearchInput}
          placeholder="입력하세요"
          height="middle"
          onSearch={handleSearch}
        />
        {centerData.length !== 0 && (
          <DefaultSelectMenu
            defaultValue={centerData[0]?.label}
            options={centerData}
            value={selectedLabel}
            size="medium"
            onChange={handleChoiceSelectBox}
          />
        )}
      </InputWrapper>
      <Wrapper>
        <>
          <ListWrapper>
            {boardList?.result === 'success' ? (
              boardSearchList && boardSearchList.length !== 0 ? (
                boardSearchList.map((item, index) => {
                  const { attachments, title, boardidx } = item;

                  return (
                    <div key={index}>
                      <MovieList attachments={attachments} title={title} boardidx={boardidx} />
                    </div>
                  );
                })
              ) : (
                <NoDataWrapper>
                  <NoSearchData />
                </NoDataWrapper>
              )
            ) : (
              Array(4)
                .fill(null)
                .map((_, index) => (
                  <DataWrapper key={index}>
                    <div></div>
                    <div></div>
                    <div></div>
                  </DataWrapper>
                ))
            )}
          </ListWrapper>
          {boardList?.data?.totalPages > 1 && (
            <PaginationWrapper>
              <Paginav
                total={boardList.data?.totalElements}
                currentPage={pageNumber}
                pageSize={boardList.data?.size}
                onPageChange={handlePageChange}
              />
            </PaginationWrapper>
          )}
        </>
      </Wrapper>
    </>
  );
};

export default Reference;

const Wrapper = styled.div`
  margin-bottom: 128px;

  .ant-skeleton-content .ant-skeleton-title {
    height: 240px;
  }

  .ant-skeleton-content .ant-skeleton-paragraph {
    width: 435px;
  }
`;

const Layout = styled.div`
  display: flex;
  justify-content: center;
  width: 890px;
  gap: 8px;
  margin: 0 auto;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 48px 20px;
`;

export const ButtonWrapper = styled(Layout)`
  justify-content: flex-start;
  padding: 24px 0 48px;
`;

export const InputWrapper = styled(Layout)`
  justify-content: space-between;
  padding-bottom: 32px;

  & .ant-input-search {
    width: 320px;
  }

  & .ant-select {
    width: 240px;
  }
`;

export const NoDataWrapper = styled(Layout)`
  padding: 96px 0 128px;
  justify-content: flex-start;
`;

export const PaginationWrapper = styled.div`
  margin: 48px 0 128px;
  display: flex;
  justify-content: end;
`;
